import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';

import { BehaviorSubject, Observable, Subscription, withLatestFrom } from 'rxjs';

import { TripType } from '@fcom/core/constants';
import { stopPropagation } from '@fcom/utils';
import { TripTypeMapping } from '@fcom/core/utils/tripType.utils';

@Component({
  selector: 'fin-trip-type-selector',
  styleUrls: ['./trip-type-selector.component.scss'],
  templateUrl: './trip-type-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TripTypeSelectorComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  disabled = false;

  @Input()
  selectedTripType$: Observable<TripType>;

  @Input()
  availableTripTypes$: Observable<TripType[]>;

  @Output()
  setTripType = new EventEmitter<TripType>();

  readonly TripType = TripType;
  readonly TripTypeMapping = TripTypeMapping;

  modalOpen = false;
  selectedElement: ElementRef = null;
  currentTripType$ = new BehaviorSubject<TripType>(TripType.RETURN);
  selectedTripTypeIndex$ = new BehaviorSubject<number>(0);
  subscription = new Subscription();

  @ViewChildren('tripTypeRadioBtn', { read: ElementRef }) tripTypeRadioBtn: QueryList<ElementRef>;

  ngOnInit(): void {
    this.subscription.add(
      this.selectedTripType$
        .pipe(withLatestFrom(this.availableTripTypes$))
        .subscribe(([tripType, availableTripTypes]) => {
          const selectedIndex = availableTripTypes.findIndex((t) => t === tripType);
          this.selectedTripTypeIndex$.next(selectedIndex);
        })
    );
  }

  ngAfterViewInit(): void {
    this.subscription.add(
      this.selectedTripTypeIndex$
        .pipe(withLatestFrom(this.availableTripTypes$))
        .subscribe(([index, availableTripTypes]) => {
          this.currentTripType$.next(availableTripTypes[index]);
          this.tripTypeRadioBtn.get(index).nativeElement?.focus();
        })
    );
  }

  handleKeyPress(selectedTripType: TripType, event: KeyboardEvent): void {
    stopPropagation(event);
    const currentIndex = this.selectedTripTypeIndex$.getValue();
    const maxIndex = this.tripTypeRadioBtn.length - 1;

    switch (event.key) {
      case 'Down':
      case 'ArrowDown':
      case 'Right':
      case 'ArrowRight':
        this.selectedTripTypeIndex$.next(currentIndex + 1 > maxIndex ? 0 : currentIndex + 1);
        break;
      case 'Up':
      case 'ArrowUp':
      case 'Left':
      case 'ArrowLeft':
        this.selectedTripTypeIndex$.next(currentIndex - 1 < 0 ? maxIndex : currentIndex - 1);
        break;
      case 'Enter':
      case ' ':
        this.onTripTypeSelect(selectedTripType, event);
        break;
      default:
        break;
    }
  }

  openModal(): void {
    this.modalOpen = true;
  }

  onTripTypeSelect(selectedTripType: TripType, event: Event): void {
    stopPropagation(event);
    this.modalOpen = false;
    this.setTripType.emit(selectedTripType);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.subscription = null;
  }
}
